import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import api from "../lib/api";
import { StrengthBadge } from "../components/Badges";
import { Loader2, FileText, Trash2, CheckCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function History() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/decisions"); setRows(data); }
    catch (e) { console.error("Failed to load decisions", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (e, id) => {
    e.stopPropagation();
    try { await api.delete(`/decisions/${id}`); setRows(rows.filter((r) => r.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Decision history</h1>
          <p className="text-slate-500 mt-1">Every decision you've analyzed, saved to your account.</p>
        </div>
        <Button onClick={() => navigate("/decisions/new")} className="bg-slate-900 hover:bg-slate-800 rounded-lg">Analyze a Decision</Button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No decisions yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="history-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left border-b border-slate-200">
                {["Decision", "Category", "Cases", "Evidence", "Conclusion", "Status", ""].map((h) => (
                  <th key={h} className="px-5 py-3 font-medium text-slate-500 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/decisions/${r.id}`)} data-testid={`history-row-${r.id}`}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors">
                  <td className="px-5 py-4 max-w-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 truncate">{r.title}</span>
                      {r.is_demo && <span className="font-mono text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0">DEMO</span>}
                      {r.implemented && <CheckCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{r.category}</td>
                  <td className="px-5 py-4 font-mono text-slate-700">{r.cases_count}</td>
                  <td className="px-5 py-4">{r.status === "complete" ? <StrengthBadge value={r.evidence_strength} /> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-5 py-4">{r.status === "complete" ? <StrengthBadge value={r.conclusion} /> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-5 py-4">
                    {r.status === "analyzing" ? <span className="flex items-center gap-1 text-xs text-blue-600"><Loader2 className="w-3 h-3 animate-spin" /> Analyzing</span>
                      : r.status === "error" ? <span className="text-xs text-rose-600">Failed</span>
                        : <span className="text-xs text-emerald-600">Complete</span>}
                  </td>
                  <td className="px-5 py-4">
                    <button onClick={(e) => del(e, r.id)} data-testid={`delete-${r.id}`} className="text-slate-300 hover:text-rose-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
